import { useTranslation } from 'react-i18next';
import { FaTimes } from 'react-icons/fa';
import { useEffect, useState } from 'react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';

interface QuotationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  quotation?: any;
  businessId: string;
}

const QuotationModal: React.FC<QuotationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  quotation,
  businessId
}) => {
  const { t } = useTranslation();
  const [formFields, setFormFields] = useState<any[]>([]);
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [loadingFields, setLoadingFields] = useState(true);

  useEffect(() => {
    const fetchFormFields = async () => {
      if (!businessId) return;
      setLoadingFields(true);
      try {
        const db = getFirestore();
        const formDoc = await getDoc(doc(db, 'QuotationForms', businessId));
        if (formDoc.exists()) {
          const data = formDoc.data();
          // Unificar todos los campos de todos los steps
          const fields = (data.steps || []).flatMap((step: any) => step.fields || []);
          setFormFields(fields);
        } else {
          setFormFields([]);
        }
      } catch (err) {
        setFormFields([]);
      } finally {
        setLoadingFields(false);
      }
    };
    if (isOpen) fetchFormFields();
  }, [businessId, isOpen]);

  useEffect(() => {
    // Inicializar valores por defecto
    if (quotation) {
      setFormValues((prev) => ({ ...prev, ...quotation }));
    }
  }, [quotation]);

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox' && e.target instanceof HTMLInputElement) {
      setFormValues((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else if (type === 'select-multiple' && e.target instanceof HTMLSelectElement) {
      const selected = Array.from(e.target.options).filter((o) => o.selected).map((o) => o.value);
      setFormValues((prev) => ({ ...prev, [name]: selected }));
    } else {
      setFormValues((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Construir array de campos personalizados
    const customFields = formFields.map(field => ({
      id: field.id,
      label: field.label,
      value: formValues[field.id]
    }));
    // Separar los campos fijos (por ejemplo, clientName, clientEmail, projectName, amount, currency, status)
    const fixedFields: Record<string, any> = {};
    ['clientName', 'clientEmail', 'projectName', 'amount', 'currency', 'status', 'clientId'].forEach(key => {
      if (formValues[key] !== undefined) fixedFields[key] = formValues[key];
    });
    const data = {
      ...fixedFields,
      customFields,
      createdAt: quotation?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    onSubmit(data);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative shadow-3xl border animate-fadeIn">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 bg-transparent border-none text-gray-500 cursor-pointer p-2 flex items-center justify-center transition-colors duration-200 hover:text-red-600"
        >
          <FaTimes />
        </button>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          {quotation ? t('quotations.edit') : t('quotations.add')}
        </h2>
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          {/* Campos personalizados dinámicos */}
          {loadingFields ? (
            <div className="text-center text-gray-500">Cargando campos personalizados...</div>
          ) : (
            formFields.map((field) => {
              const value = formValues[field.id] ?? '';
              if (field.type === 'text' || field.type === 'number') {
                return (
                  <div key={field.id} className="flex flex-col gap-2">
                    <label htmlFor={field.id} className="text-gray-900 font-medium">{field.label}</label>
                    <input
                      type={field.type}
                      id={field.id}
                      name={field.id}
                      value={value}
                      required={field.required}
                      onChange={handleChange}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-red-600"
                    />
                  </div>
                );
              }
              if (field.type === 'textarea') {
                return (
                  <div key={field.id} className="flex flex-col gap-2">
                    <label htmlFor={field.id} className="text-gray-900 font-medium">{field.label}</label>
                    <textarea
                      id={field.id}
                      name={field.id}
                      value={value}
                      required={field.required}
                      onChange={handleChange}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-red-600"
                    />
                  </div>
                );
              }
              if (field.type === 'select') {
                return (
                  <div key={field.id} className="flex flex-col gap-2">
                    <label htmlFor={field.id} className="text-gray-900 font-medium">{field.label}</label>
                    <select
                      id={field.id}
                      name={field.id}
                      value={value}
                      required={field.required}
                      onChange={handleChange}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-red-600 bg-white"
                    >
                      <option value="">Selecciona una opción</option>
                      {(field.options || []).map((opt: string, idx: number) => (
                        <option key={idx} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                );
              }
              if (field.type === 'multiselect') {
                return (
                  <div key={field.id} className="flex flex-col gap-2">
                    <label htmlFor={field.id} className="text-gray-900 font-medium">{field.label}</label>
                    <select
                      id={field.id}
                      name={field.id}
                      multiple
                      value={value || []}
                      required={field.required}
                      onChange={handleChange}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-red-600 bg-white"
                    >
                      {(field.options || []).map((opt: string, idx: number) => (
                        <option key={idx} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                );
              }
              if (field.type === 'date') {
                return (
                  <div key={field.id} className="flex flex-col gap-2">
                    <label htmlFor={field.id} className="text-gray-900 font-medium">{field.label}</label>
                    <input
                      type="date"
                      id={field.id}
                      name={field.id}
                      value={value}
                      required={field.required}
                      onChange={handleChange}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-red-600"
                    />
                  </div>
                );
              }
              return null;
            })
          )}
          {/* Botón submit */}
          <button 
            type="submit"
            className="bg-red-600 hover:bg-red-700 text-white border-none px-6 py-3 rounded-lg cursor-pointer text-base font-medium transition-colors duration-200"
          >
            {quotation ? t('quotations.update') : t('quotations.create')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default QuotationModal; 